<template>
  <transition name="confirm-dialog">
    <div
      v-if="visible"
      class="confirm-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      @keydown.esc.prevent="onCancel"
    >
      <button
        type="button"
        class="confirm-dialog__scrim"
        aria-label="关闭"
        @click="onCancel"
      />
      <div
        class="confirm-dialog__card"
        :class="'confirm-dialog__card--' + variant"
      >
        <div
          class="confirm-dialog__icon"
          :class="'confirm-dialog__icon--' + variant"
          aria-hidden="true"
        >
          <svg v-if="variant === 'danger'" viewBox="0 0 24 24" width="28" height="28">
            <path
              fill="currentColor"
              d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
            />
          </svg>
          <svg v-else viewBox="0 0 24 24" width="28" height="28">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            />
          </svg>
        </div>
        <h2 class="confirm-dialog__title">{{ title }}</h2>
        <p class="confirm-dialog__message">{{ message }}</p>
        <div class="confirm-dialog__actions">
          <button
            type="button"
            class="confirm-dialog__btn confirm-dialog__btn--ghost"
            :disabled="loading"
            @click="onCancel"
          >
            {{ cancelText }}
          </button>
          <button
            type="button"
            class="confirm-dialog__btn confirm-dialog__btn--primary"
            :class="'confirm-dialog__btn--' + variant"
            :disabled="loading"
            @click="onConfirm"
          >
            {{ loading ? loadingText : confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ConfirmDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '请确认'
    },
    message: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    loadingText: {
      type: String,
      default: '处理中…'
    },
    loading: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'danger'].includes(v)
    }
  },
  watch: {
    visible (open) {
      if (open) {
        this.$nextTick(() => {
          document.addEventListener('keydown', this.onKeydown)
        })
      } else {
        document.removeEventListener('keydown', this.onKeydown)
      }
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (e) {
      if (e.key === 'Escape' && this.visible && !this.loading) {
        this.onCancel()
      }
    },
    onCancel () {
      if (this.loading) return
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    onConfirm () {
      if (this.loading) return
      this.$emit('confirm')
    }
  }
}
</script>

<style scoped>
.confirm-dialog {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.confirm-dialog__scrim {
  position: absolute;
  inset: 0;
  border: none;
  background: rgba(8, 24, 48, 0.55);
  backdrop-filter: blur(2px);
  cursor: pointer;
}

.confirm-dialog__card {
  position: relative;
  z-index: 1;
  width: min(380px, 100%);
  padding: 28px 24px 22px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 20px 56px rgba(13, 71, 161, 0.28);
  text-align: center;
}

.confirm-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto 14px;
  border-radius: 50%;
}

.confirm-dialog__icon--default {
  background: #e3f2fd;
  color: #1565c0;
}

.confirm-dialog__icon--danger {
  background: #ffebee;
  color: #c62828;
}

.confirm-dialog__title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #0d47a1;
}

.confirm-dialog__card--danger .confirm-dialog__title {
  color: #b71c1c;
}

.confirm-dialog__message {
  margin: 0 0 24px;
  font-size: 15px;
  line-height: 1.6;
  color: #546e7a;
}

.confirm-dialog__actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.confirm-dialog__btn {
  min-width: 108px;
  padding: 10px 20px;
  border-radius: 10px;
  border: 2px solid transparent;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.12s ease, transform 0.12s ease;
}

.confirm-dialog__btn:active:not(:disabled) {
  transform: scale(0.98);
}

.confirm-dialog__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.confirm-dialog__btn--ghost {
  background: #f5f7fa;
  color: #546e7a;
  border-color: #cfd8dc;
}

.confirm-dialog__btn--ghost:hover:not(:disabled) {
  background: #eceff1;
}

.confirm-dialog__btn--primary.confirm-dialog__btn--default {
  background: linear-gradient(180deg, #42a5f5 0%, #1565c0 100%);
  color: #fff;
  border-color: #1565c0;
}

.confirm-dialog__btn--primary.confirm-dialog__btn--danger {
  background: linear-gradient(180deg, #ef5350 0%, #c62828 100%);
  color: #fff;
  border-color: #c62828;
}

.confirm-dialog-enter-active,
.confirm-dialog-leave-active {
  transition: opacity 0.2s ease;
}

.confirm-dialog-enter-active .confirm-dialog__card,
.confirm-dialog-leave-active .confirm-dialog__card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.confirm-dialog-enter,
.confirm-dialog-leave-to {
  opacity: 0;
}

.confirm-dialog-enter .confirm-dialog__card,
.confirm-dialog-leave-to .confirm-dialog__card {
  opacity: 0;
  transform: scale(0.94) translateY(8px);
}
</style>



// WEBPACK FOOTER //
// src/components/ConfirmDialog.vue